import { useState, useEffect, useCallback } from 'react'

const RECENT_ORGANIZATIONS_KEY = 'hubble_recent_visited_organizations'
const MAX_RECENT_ORGANIZATIONS = 10

interface RecentOrganization {
  id: string
  name: string
  timestamp: number
}

interface UseRecentOrganizationsReturn {
  recentOrganizations: RecentOrganization[]
  addRecentOrganization: (organization: { id: string; name: string }) => void
}

const useRecentOrganizations = (): UseRecentOrganizationsReturn => {
  const [recentOrganizations, setRecentOrganizations] = useState<
    RecentOrganization[]
  >([])

  const loadFromStorage = useCallback(() => {
    try {
      const stored = localStorage.getItem(RECENT_ORGANIZATIONS_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          setRecentOrganizations((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
              return parsed
            }
            return prev
          })
        }
      }
    } catch (error) {
      console.error('Error loading recent organizations:', error)
    }
  }, [])

  useEffect(() => {
    loadFromStorage()

    const interval = setInterval(() => {
      loadFromStorage()
    }, 1000)

    return () => clearInterval(interval)
  }, [loadFromStorage])

  useEffect(() => {
    if (recentOrganizations.length > 0) {
      try {
        localStorage.setItem(
          RECENT_ORGANIZATIONS_KEY,
          JSON.stringify(recentOrganizations)
        )
      } catch (error) {
        console.error('Error saving recent organizations:', error)
      }
    }
  }, [recentOrganizations])

  const addRecentOrganization = useCallback(
    (organization: { id: string; name: string }) => {
      if (!organization.id) return

      setRecentOrganizations((prev) => {
        const filtered = prev.filter((org) => org.id !== organization.id)
        const newEntry: RecentOrganization = {
          ...organization,
          timestamp: Date.now(),
        }
        const updated = [newEntry, ...filtered]
        return updated.slice(0, MAX_RECENT_ORGANIZATIONS)
      })
    },
    []
  )

  return {
    recentOrganizations,
    addRecentOrganization,
  }
}

export default useRecentOrganizations
